import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// "On this day" — resurfaces entries from the same calendar date in
// previous years (up to 3 back) across the modules where a memory
// means something: check-in notes, weight, journey entries, moments
// prompts and finished books. Pure reads, no backend.

const YEARS_BACK = 3

function pastDates() {
  const now = new Date()
  const dates = []
  for (let i = 1; i <= YEARS_BACK; i += 1) {
    const y = now.getFullYear() - i
    const mm = String(now.getMonth() + 1).padStart(2, '0')
    const dd = String(now.getDate()).padStart(2, '0')
    // Feb 29 in a non-leap year just won't match anything — fine.
    dates.push(`${y}-${mm}-${dd}`)
  }
  return dates
}

export function useOnThisDay() {
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) { setLoading(false); return undefined }
    let alive = true
    const dates = pastDates()
    const dayWindows = dates.map((d) => ({ from: `${d}T00:00:00`, to: `${d}T23:59:59`, date: d }))

    Promise.all([
      supabase.from('recovery_checkins')
        .select('date, mood, energy, notes')
        .eq('user_id', user.id)
        .in('date', dates)
        .not('notes', 'is', null),
      supabase.from('weight_entries')
        .select('date, weight, note')
        .eq('user_id', user.id)
        .in('date', dates),
      // journey_entries / moments are timestamp-keyed — one ranged
      // query per past year keeps it correct across timezones-ish.
      ...dayWindows.map((w) => supabase.from('journey_entries')
        .select('recorded_at, summary, transcript')
        .eq('user_id', user.id)
        .gte('recorded_at', w.from)
        .lte('recorded_at', w.to)),
      supabase.from('moments_prompts')
        .select('id, prompt_date, ai_caption, ai_caption_ar')
        .in('prompt_date', dates),
      supabase.from('reading_books')
        .select('title, author, finished_at')
        .eq('user_id', user.id)
        .in('finished_at', dates),
    ]).then((results) => {
      if (!alive) return
      const found = []
      const [checkins, weights, ...rest] = results
      const journeys = rest.slice(0, dayWindows.length)
      const moments = rest[dayWindows.length]
      const books = rest[dayWindows.length + 1]

      for (const r of checkins.data || []) {
        found.push({ kind: 'checkin', date: r.date, text: r.notes, meta: { mood: r.mood, energy: r.energy } })
      }
      for (const r of weights.data || []) {
        found.push({ kind: 'weight', date: r.date, text: r.note, meta: { weight: r.weight } })
      }
      journeys.forEach((res, i) => {
        for (const r of res.data || []) {
          found.push({ kind: 'journey', date: dayWindows[i].date, text: r.summary || r.transcript?.slice(0, 100) })
        }
      })
      for (const r of moments?.data || []) {
        found.push({ kind: 'moment', date: r.prompt_date, text: r.ai_caption_ar || r.ai_caption, meta: { promptId: r.id } })
      }
      for (const r of books?.data || []) {
        found.push({ kind: 'book', date: r.finished_at, text: r.title, meta: { author: r.author } })
      }

      found.sort((a, b) => (a.date < b.date ? 1 : -1))
      setItems(found)
      setLoading(false)
    }).catch(() => { if (alive) setLoading(false) })

    return () => { alive = false }
  }, [user])

  return { items, loading }
}
